import { useMutation } from '@base-app/web2'
import { memo, type ReactNode, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CREATE_FEED_POST, UPDATE_FEED_POST } from '../../../graphql/feed/mutations'
import { FEED_POST_QUERY } from '../../../graphql/feed/queries'
import type { FeedPost } from '../../../graphql/feed/types'
import { useRecordById } from '../../../hooks/useRecordById'
import { useToast } from '../../../hooks/useToast'
import { prependToLists } from '../../../lib/cacheLists'
import { focusFieldById, isRichTextTooLongError, MAX_RICH_TEXT_LENGTH } from '../../../lib/richText'
import { lowResPathFor } from '../../../lib/uploads'
import { Button, FormActions } from '../../atoms'
import { FormField, FormLoadState, ImageUploadField, RichTextField } from '../../molecules'

export const FeedPostRetentionNoticeIcon = memo(() => {
  const { t } = useTranslation('admin-mobifull')
  return (
    <span
      role="img"
      className="feed-info-icon"
      // biome-ignore lint/a11y/noNoninteractiveTabindex: focusable tooltip icon for keyboard access
      tabIndex={0}
      title={t('feed.form.retentionNotice')}
      aria-label={t('feed.form.retentionNotice')}>
      ℹ
    </span>
  )
})

FeedPostRetentionNoticeIcon.displayName = 'FeedPostRetentionNoticeIcon'

export const FeedPostModalTitle = memo(({ isEdit }: { isEdit?: boolean }) => {
  const { t } = useTranslation('admin-mobifull')
  return (
    <span className="feed-modal-title">
      <span>{isEdit ? t('feed.editTitle') : t('feed.newPost')}</span>
      <FeedPostRetentionNoticeIcon />
    </span>
  )
})

FeedPostModalTitle.displayName = 'FeedPostModalTitle'

export interface FeedPostFormFieldsProps {
  feedPost?: FeedPost
  clientId: string
  appId: string
  onSuccess: () => void
  onClose: () => void
  // Renders every field as its pulsing placeholder while the record loads
  skeleton?: boolean
  showNotice?: boolean
  title?: ReactNode
}

export const FeedPostFormFields = memo(
  ({
    feedPost,
    clientId,
    appId,
    onSuccess,
    onClose,
    skeleton,
    showNotice,
    title: customTitle,
  }: FeedPostFormFieldsProps) => {
    const { t } = useTranslation('admin-mobifull')
    const { showToast } = useToast()
    const isEdit = !!feedPost
    const [title, setTitle] = useState(feedPost?.title ?? '')
    const [content, setContent] = useState(feedPost?.content ?? '')
    const [image, setImage] = useState(feedPost?.image ?? '')
    const [uploadingImage, setUploadingImage] = useState(false)
    const [errors, setErrors] = useState<{ title?: string; content?: string }>({})

    const makeOptimistic = useCallback(
      (id: string): FeedPost => ({
        __typename: 'FeedPost',
        id,
        client_id: clientId,
        application_id: appId,
        title,
        content: content || null,
        image: image || null,
        image_low_res: lowResPathFor(image),
        published_at: feedPost?.published_at ?? new Date().toISOString(),
        created_at: feedPost?.created_at ?? new Date().toISOString(),
        updated_at: new Date().toISOString(),
      }),
      [clientId, appId, title, content, image, feedPost?.published_at, feedPost?.created_at],
    )

    // The API's refusal belongs to the field; anything else is a toast
    const handleError = useCallback(
      (err: Error) => {
        if (isRichTextTooLongError(err)) {
          setErrors(current => ({ ...current, content: t('feed.form.contentTooLong') }))
          focusFieldById('feed-post-content')
        } else {
          showToast(err.message, 'error')
        }
      },
      [showToast, t],
    )

    const [createFeedPost, { loading: creating }] = useMutation(CREATE_FEED_POST, {
      optimisticResponse: { createFeedPost: makeOptimistic(`temp-${Date.now()}`) },
      update(cache, result) {
        const item = result.data?.createFeedPost
        if (!item) {
          return
        }
        prependToLists(cache, 'feedPosts', item, { clientId, applicationId: appId })
      },
      onError: handleError,
      onCompleted() {
        onSuccess()
      },
    })

    const [updateFeedPost, { loading: updating }] = useMutation(UPDATE_FEED_POST, {
      optimisticResponse: { updateFeedPost: makeOptimistic(feedPost?.id ?? '') },
      onError: handleError,
      onCompleted() {
        onSuccess()
      },
    })

    const loading = creating || updating || uploadingImage

    const validate = useCallback(() => {
      const errs: { title?: string; content?: string } = {}
      if (!title.trim()) {
        errs.title = t('common.form.titleRequired')
      }
      if (content.length > MAX_RICH_TEXT_LENGTH) {
        errs.content = t('feed.form.contentTooLong')
      }
      setErrors(errs)
      return Object.keys(errs).length === 0
    }, [title, content, t])

    const handleSubmit = useCallback(
      (e: React.FormEvent) => {
        e.preventDefault()
        if (!validate()) {
          return
        }
        const vars = {
          title,
          content: content || null,
          image: image || null,
        }
        if (isEdit) {
          updateFeedPost({ variables: { id: feedPost.id, ...vars } })
        } else {
          createFeedPost({ variables: { client_id: clientId, application_id: appId, ...vars } })
        }
      },
      [
        validate,
        isEdit,
        feedPost?.id,
        title,
        content,
        image,
        clientId,
        appId,
        updateFeedPost,
        createFeedPost,
      ],
    )

    return (
      <form onSubmit={handleSubmit} noValidate aria-busy={skeleton}>
        {skeleton ? (
          <p className="sr-only" role="status">
            {t('common.loading')}
          </p>
        ) : null}
        {customTitle ? <div className="feed-form-title">{customTitle}</div> : null}
        {showNotice ? (
          <div className="feed-post-form__header">
            <FeedPostRetentionNoticeIcon />
          </div>
        ) : null}
        <FormField
          skeleton={skeleton}
          id="feed-post-title"
          label={t('feed.form.titleLabel')}
          name="title"
          value={title}
          onChange={e => setTitle(e.target.value)}
          error={errors.title}
          required
        />
        <ImageUploadField
          skeleton={skeleton}
          applicationId={appId}
          id="feed-post-image"
          target="feed"
          label={t('feed.form.imageLabel')}
          value={image}
          onChange={setImage}
          onUploadingChange={setUploadingImage}
        />
        <RichTextField
          skeleton={skeleton}
          id="feed-post-content"
          label={t('feed.form.contentLabel')}
          value={content}
          onChange={setContent}
          error={errors.content}
        />
        <FormActions>
          <Button type="button" variant="secondary" onClick={onClose} disabled={loading}>
            {t('common.actions.cancel')}
          </Button>
          <Button skeleton={skeleton} type="submit" disabled={loading}>
            {loading ? t('feed.form.saving') : t('feed.form.save')}
          </Button>
        </FormActions>
      </form>
    )
  },
)

FeedPostFormFields.displayName = 'FeedPostFormFields'

export interface FeedPostFormProps extends Omit<FeedPostFormFieldsProps, 'feedPost'> {
  // The record to edit, by id; omit it to create a new one
  id?: string
}

// Loads the record by id (from the cache when the list has it, otherwise from the API)
export const FeedPostForm = memo(({ id, ...props }: FeedPostFormProps) => {
  const { record, status } = useRecordById(FEED_POST_QUERY, id, data => data?.feedPost)
  if (status === 'loading') {
    return <FeedPostFormFields skeleton {...props} />
  }
  if (status !== 'new' && status !== 'ready') {
    return <FormLoadState status={status} onClose={props.onClose} />
  }
  return <FeedPostFormFields key={id ?? 'new'} feedPost={record} {...props} />
})

FeedPostForm.displayName = 'FeedPostForm'
